import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import {
  getRecentlyViewed,
  removeRecentlyViewed,
  clearRecentlyViewed,
  EVENT_NAME,
} from '../../../utils/recentlyViewed';

const formatRent = (price) => {
  if (!price || !Number.isFinite(Number(price))) return 'Contact for price';
  return `₹${Number(price).toLocaleString('en-IN')}`;
};

const RecentlyViewedStrip = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem('tolet_recently_viewed_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const syncItems = () => {
    setItems(getRecentlyViewed());
  };

  useEffect(() => {
    syncItems();

    const handleCustomChange = () => syncItems();
    const handleStorageChange = (e) => {
      if (e.key === 'tolet_recently_viewed') syncItems();
    };

    window.addEventListener(EVENT_NAME, handleCustomChange);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener(EVENT_NAME, handleCustomChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem('tolet_recently_viewed_collapsed', String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const handleCardClick = (id) => {
    if (!id) return;
    navigate(`/property-detail-experience/${id}`);
  };

  const handleRemove = (e, id) => {
    e.stopPropagation();
    removeRecentlyViewed(id);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    clearRecentlyViewed();
  };

  if (!items || items.length === 0) {
    return null;
  }

  // Collapsed Minimalist Pill Strip
  if (isCollapsed) {
    return (
      <div className="sticky top-16 z-30 w-full bg-white/95 backdrop-blur-md border-b border-gray-200/80 shadow-2xs transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between">
          <button
            type="button"
            onClick={toggleCollapse}
            className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 hover:text-rose-600 transition cursor-pointer"
          >
            <span className="w-5 h-5 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
              <Icon name="Clock" size={12} />
            </span>
            <span>
              Continue your search: <strong className="text-gray-900">{items.length}</strong> recently viewed {items.length === 1 ? 'place' : 'places'}
            </span>
            <Icon name="ChevronDown" size={14} className="text-gray-400 ml-0.5" />
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClearAll}
              className="text-[11px] text-gray-400 hover:text-red-500 transition cursor-pointer"
              title="Clear recently viewed history"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sticky top-16 z-30 w-full bg-gradient-to-r from-slate-900 via-gray-900 to-slate-900 text-white shadow-md border-b border-gray-800 animate-in fade-in slide-in-from-top-2 duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3">
        {/* Strip Header */}
        <div className="flex items-center justify-between gap-3 mb-2.5">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center justify-center shrink-0">
              <Icon name="Clock" size={13} />
            </span>
            <span className="text-xs sm:text-sm font-bold tracking-tight text-white flex items-center gap-1.5">
              Pick up where you left off
              <span className="hidden sm:inline-block text-[11px] font-normal text-gray-400">
                • {items.length} recently viewed
              </span>
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleClearAll}
              className="text-[11px] text-gray-400 hover:text-rose-300 font-medium transition cursor-pointer flex items-center gap-1"
            >
              <Icon name="Trash2" size={12} />
              <span className="hidden xs:inline">Clear all</span>
            </button>

            <button
              type="button"
              onClick={toggleCollapse}
              className="w-6 h-6 rounded-md bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white flex items-center justify-center transition cursor-pointer"
              aria-label="Minimize recently viewed strip"
              title="Minimize"
            >
              <Icon name="ChevronDown" size={14} className="rotate-180" />
            </button>
          </div>
        </div>

        {/* Horizontal Property Carousel / Strip */}
        <div className="flex items-center gap-2.5 sm:gap-3 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent snap-x">
          {items.map((item) => (
            <div
              key={item.id}
              onClick={() => handleCardClick(item.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCardClick(item.id);
                }
              }}
              className="group relative flex items-center gap-2.5 p-1.5 pr-3 sm:pr-4 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 hover:border-rose-400/50 transition-all duration-200 cursor-pointer shrink-0 snap-start max-w-[260px] sm:max-w-[280px]"
            >
              {/* Thumbnail Image */}
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden bg-gray-800 shrink-0 relative border border-white/10">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    <Icon name="Home" size={18} />
                  </div>
                )}
              </div>

              {/* Text Info */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs sm:text-[13px] font-bold text-white group-hover:text-rose-400 transition-colors">
                    {formatRent(item.rent)}
                    <span className="text-[10px] font-normal text-gray-400 ml-0.5">/mo</span>
                  </span>
                </div>

                <p className="text-[11px] sm:text-xs text-gray-200 font-medium truncate mt-0.5">
                  {item.title}
                </p>

                <p className="text-[10px] text-gray-400 truncate flex items-center gap-1 mt-0.5">
                  {item.bhk && <span className="font-semibold text-rose-300">{item.bhk}</span>}
                  {item.bhk && item.city && <span>•</span>}
                  <span className="truncate">{item.city}</span>
                </p>
              </div>

              {/* Remove single item button */}
              <button
                type="button"
                onClick={(e) => handleRemove(e, item.id)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded-full text-gray-400 hover:text-white hover:bg-white/20 transition-all cursor-pointer shrink-0 ml-0.5"
                title="Remove from history"
                aria-label="Remove from history"
              >
                <Icon name="X" size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RecentlyViewedStrip;
